import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { appConfig } from '../config/AppConfig';
import { useChatHistory } from './useChatHistory';

// Chat context
const ChatContext = createContext();

// Initial state
const initialState = {
  messages: [],
  isProcessing: false,
  processingStep: '',
  currentInput: '',
  dbId: 'kfc_postgres',
  socket: null,
  connected: false,
  error: null,
  streamUpdates: [],
  currentSteps: [],
  currentContext: null,  // Context for multi-turn conversations
  // Goal Mode state
  chatMode: 'normal',           // 'normal' | 'goal'
  goalText: '',                 // User's goal description
  goalSuggestions: [],          // [{label, query}]
  isLoadingGoalSuggestions: false,
  goalExplanation: ''           // LLM explanation of suggestions
};

// Action types
const ACTIONS = {
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  SET_PROCESSING: 'SET_PROCESSING',
  SET_PROCESSING_STEP: 'SET_PROCESSING_STEP',
  SET_CURRENT_INPUT: 'SET_CURRENT_INPUT',
  SET_DB_ID: 'SET_DB_ID',
  SET_SOCKET: 'SET_SOCKET',
  SET_CONNECTED: 'SET_CONNECTED',
  SET_ERROR: 'SET_ERROR',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  ADD_STREAM_UPDATE: 'ADD_STREAM_UPDATE',
  CLEAR_STREAM_UPDATES: 'CLEAR_STREAM_UPDATES',
  ADD_STEP: 'ADD_STEP',
  CLEAR_STEPS: 'CLEAR_STEPS',
  SET_CONTEXT: 'SET_CONTEXT',
  // Goal Mode actions
  SET_CHAT_MODE: 'SET_CHAT_MODE',
  SET_GOAL_TEXT: 'SET_GOAL_TEXT',
  SET_GOAL_SUGGESTIONS: 'SET_GOAL_SUGGESTIONS',
  SET_LOADING_GOAL_SUGGESTIONS: 'SET_LOADING_GOAL_SUGGESTIONS',
  CLEAR_GOAL_STATE: 'CLEAR_GOAL_STATE'
};

// Reducer
function chatReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };
    case ACTIONS.ADD_MESSAGE:
      const newMessages = [...state.messages, action.payload];
      if (newMessages.length > 50) {
        newMessages.splice(0, newMessages.length - 50);
      }
      return { ...state, messages: newMessages };
    case ACTIONS.UPDATE_MESSAGE:
      const updatedMessages = state.messages.map(msg => 
        msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
      );
      return { ...state, messages: updatedMessages };
    case ACTIONS.SET_PROCESSING:
      return { ...state, isProcessing: action.payload };
    case ACTIONS.SET_PROCESSING_STEP:
      return { ...state, processingStep: action.payload };
    case ACTIONS.SET_CURRENT_INPUT:
      return { ...state, currentInput: action.payload };
    case ACTIONS.SET_DB_ID:
      return { ...state, dbId: action.payload };
    case ACTIONS.SET_SOCKET:
      return { ...state, socket: action.payload };
    case ACTIONS.SET_CONNECTED:
      return { ...state, connected: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    case ACTIONS.CLEAR_MESSAGES:
      return { ...state, messages: [] };
    case ACTIONS.ADD_STREAM_UPDATE:
      return { ...state, streamUpdates: [...state.streamUpdates, action.payload] };
    case ACTIONS.CLEAR_STREAM_UPDATES:
      return { ...state, streamUpdates: [] };
    case ACTIONS.ADD_STEP:
      return { ...state, currentSteps: [...state.currentSteps, action.payload] };
    case ACTIONS.CLEAR_STEPS:
      return { ...state, currentSteps: [] };
    case ACTIONS.SET_CONTEXT:
      return { ...state, currentContext: action.payload };
    // Goal Mode reducers
    case ACTIONS.SET_CHAT_MODE:
      return { ...state, chatMode: action.payload };
    case ACTIONS.SET_GOAL_TEXT:
      return { ...state, goalText: action.payload };
    case ACTIONS.SET_GOAL_SUGGESTIONS:
      return {
        ...state,
        goalSuggestions: action.payload.suggestions,
        goalExplanation: action.payload.explanation,
        isLoadingGoalSuggestions: false
      };
    case ACTIONS.SET_LOADING_GOAL_SUGGESTIONS:
      return { ...state, isLoadingGoalSuggestions: action.payload };
    case ACTIONS.CLEAR_GOAL_STATE:
      return {
        ...state,
        goalText: '',
        goalSuggestions: [],
        goalExplanation: '',
        isLoadingGoalSuggestions: false
      };
    default:
      return state;
  }
}

// Helper function to create message objects
const createMessage = (content, isUser = false, data = {}) => ({
  id: Date.now() + Math.random(),
  content,
  isUser,
  timestamp: new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }),
  ...data
});

// Helper function to build conversation history for multi-turn reformulation
// Returns last 10 Q&A pairs, with full detail for last 3 and truncated for older
const buildConversationHistory = (messages) => {
  // Extract Q&A pairs (user question followed by bot response)
  const pairs = [];

  for (let i = 0; i < messages.length - 1; i++) {
    const current = messages[i];
    const next = messages[i + 1];

    // Check if this is a user message followed by a bot response
    if (current.isUser && !next.isUser) {
      pairs.push({
        question: current.content,
        sql_query: next.sql_query || null,
        column_names: next.column_names || null,
        final_answer: next.content || null,
        is_truncated: false
      });
      i++; // Skip the bot message since we've processed it
    }
  }

  // Apply sliding window: keep last 10
  const last10 = pairs.slice(-10);

  // Apply truncation: full content for last 3, truncated for 4-10
  return last10.map((turn, idx) => {
    const distanceFromEnd = last10.length - 1 - idx;

    if (distanceFromEnd >= 3) {
      // Truncate older messages (4th from end and older)
      return {
        question: turn.question ? turn.question.slice(0, 50) + (turn.question.length > 50 ? '...' : '') : '',
        final_answer: turn.final_answer ? turn.final_answer.slice(0, 50) + (turn.final_answer.length > 50 ? '...' : '') : '',
        is_truncated: true
      };
    }

    // Full content for last 3
    return turn;
  });
};

// Chat provider component
export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const messagesEndRef = useRef(null);

  // Chat history persistence - save messages to database
  const {
    addMessage: saveMessageToDb,
    getOrCreateSession,
    currentSessionId,
    setCurrentSessionId
  } = useChatHistory();

  // Use ref to access latest chat history functions in WebSocket handler
  // IMPORTANT: activeSessionId is updated IMMEDIATELY when session is created
  // to avoid timing issues with WebSocket responses
  const chatHistoryRef = useRef({ saveMessageToDb, getOrCreateSession, currentSessionId, activeSessionId: null });
  useEffect(() => {
    chatHistoryRef.current = {
      ...chatHistoryRef.current,
      saveMessageToDb,
      getOrCreateSession,
      currentSessionId,
      // IMPORTANT: Sync activeSessionId with currentSessionId when it changes
      // This ensures chart adjustments in loaded sessions are saved correctly
      activeSessionId: currentSessionId || chatHistoryRef.current.activeSessionId
    };
  }, [saveMessageToDb, getOrCreateSession, currentSessionId]);

  // Initialize WebSocket connection
  useEffect(() => {
    const initializeConnection = async () => {
      try {
        await appConfig.load();
        const mainApiUrl = appConfig.getMainApiUrl();
        
        if (!mainApiUrl) {
          console.error('Main API URL not configured');
          dispatch({ type: ACTIONS.SET_ERROR, payload: 'Configuration error: Main API URL not configured' });
          return;
        }
    
    let reconnectTimer = null;
    let isIntentionallyClosed = false;
    let retryCount = 0;
    const maxRetries = 5;

        const connectWebSocket = () => {
          const wsUrl = appConfig.getWebSocketUrl();
          console.log('Connecting to WebSocket:', wsUrl, `(attempt ${retryCount + 1})`);

      const socket = new WebSocket(wsUrl);

      // Add connection timeout for server environments (10 seconds)
      const connectionTimeout = setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING) {
          console.log('WebSocket connection timeout, closing...');
          socket.close();
        }
      }, 10000);

      socket.onopen = () => {
        console.log('Connected to WebSocket server');
        clearTimeout(connectionTimeout); // Clear timeout on successful connection
        dispatch({ type: ACTIONS.SET_CONNECTED, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR, payload: null });
        isIntentionallyClosed = false;
        retryCount = 0; // Reset retry count on successful connection
      };

      socket.onclose = () => {
        console.log('Disconnected from WebSocket server');
        clearTimeout(connectionTimeout); // Clear timeout on close
        dispatch({ type: ACTIONS.SET_CONNECTED, payload: false });

        // Auto-reconnect if not intentionally closed and haven't exceeded max retries
        if (!isIntentionallyClosed && retryCount < maxRetries) {
          // Progressive backoff: 2s, 4s, 8s, 16s, 32s (max 30s)
          const delay = Math.min(2000 * Math.pow(2, retryCount), 30000);
          console.log(`Attempting to reconnect in ${delay/1000} seconds... (attempt ${retryCount + 1}/${maxRetries})`);

          reconnectTimer = setTimeout(() => {
            retryCount++;
            connectWebSocket();
          }, delay);
        } else if (retryCount >= maxRetries) {
          console.error('Max reconnection attempts reached. Please refresh the page.');
          dispatch({ type: ACTIONS.SET_ERROR, payload: 'Connection lost. Please refresh the page.' });
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to connect to server' });
      };

      // Handle incoming messages
      socket.onmessage = (event) => {
        // T5: Capture receive timestamp immediately
        const receiveTimestamp = performance.now();
        const receiveIsoTime = new Date().toISOString();

        try {
          const data = JSON.parse(event.data);

          if (process.env.NODE_ENV === 'development') {
            console.log('WebSocket message received:', data);
          }

          const { status, step, message, final_result, timestamp } = data;

          // Handle different message types based on status
          if (status === 'connected') {
            // Welcome message - already handled in onopen
            return;
          }

          if (status === 'success' && final_result) {
            // Final result received
            // DEBUG: Log reformulation info
            if (final_result.data.reformulation) {
              console.log('🔄 Reformulation received:', final_result.data.reformulation);
            }

            // Merge client timing from server with local receive timing
            const clientTiming = {
              ...(final_result.data.client_timing || {}),
              ws_receive_at: receiveIsoTime
            };
            // Log end-to-end timing if available
            if (clientTiming.user_click_at) {
              console.log('⏱️ Client timing:', clientTiming);
            }

            const responseMessage = createMessage(
              final_result.data.final_answer || 'Query completed successfully',
              false,
              {
                ...final_result.data,
                client_timing: clientTiming
              }
            );
            console.log('📦 Message created with reformulation:', responseMessage.reformulation);
            dispatch({ type: ACTIONS.ADD_MESSAGE, payload: responseMessage });
            dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
            dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: '' });
            dispatch({ type: ACTIONS.CLEAR_STEPS }); // Clear processing steps history

            // NEW: Save context for next turn if provided
            if (final_result.data.context) {
              console.log('💾 Saving context for next turn:', final_result.data.context);
              dispatch({ type: ACTIONS.SET_CONTEXT, payload: final_result.data.context });
            }

            // Save bot message to database for chat history persistence
            // Include ALL data needed to fully render the message on reload
            // Use activeSessionId which is updated immediately when session is created
            const { saveMessageToDb, activeSessionId } = chatHistoryRef.current;
            if (activeSessionId && saveMessageToDb) {
              console.log('💾 Saving bot message to session:', activeSessionId);
              // Ensure integer types for API validation (422 error fix)
              const execMs = final_result.meta?.query_duration_ms;
              const rowCount = final_result.data.results?.length;
              saveMessageToDb({
                role: 'assistant',
                content: final_result.data.final_answer || 'Query completed successfully',
                sql_query: final_result.data.sql_query || null,
                sql_execution_ms: execMs != null ? Math.round(execMs) : null,
                sql_row_count: rowCount != null ? parseInt(rowCount, 10) : null,
                chart_spec: final_result.data.visualization || null,
                metadata: {
                  // Data table
                  results: final_result.data.results || null,
                  column_names: final_result.data.column_names || null,
                  // Explanations
                  explanation: final_result.data.explanation || null,
                  // Suggestions for follow-up questions
                  suggestions: final_result.data.suggestions || null,
                  // Reformulation info
                  reformulation: final_result.data.reformulation || null,
                  // Context for multi-turn
                  context: final_result.data.context || null,
                  // Client timing for end-to-end tracing
                  client_timing: clientTiming || null
                }
              }, activeSessionId).catch(err => console.warn('Failed to save bot message to history:', err));
            }

            return;
          }

          if (status === 'error') {
            // Error occurred
            const errorMessage = createMessage(
              `Error: ${message || 'Unknown error occurred'}`,
              false
            );
            dispatch({ type: ACTIONS.ADD_MESSAGE, payload: errorMessage });
            dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
            dispatch({ type: ACTIONS.SET_ERROR, payload: message || 'Query processing failed' });
            dispatch({ type: ACTIONS.CLEAR_STEPS }); // Clear processing steps history
            return;
          }

          if (status === 'interrupted') {
            // Processing was interrupted
            const interruptMessage = createMessage(
              message || 'Query processing was interrupted',
              false
            );
            dispatch({ type: ACTIONS.ADD_MESSAGE, payload: interruptMessage });
            dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
            dispatch({ type: ACTIONS.CLEAR_STEPS }); // Clear processing steps history
            return;
          }

          // Handle streaming updates
          const update = {
            id: Date.now() + Math.random(),
            timestamp: new Date().toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            }),
            status,
            step: step || '',
            message: message || '',
            data: data.data || {}
          };
          
          dispatch({ type: ACTIONS.ADD_STREAM_UPDATE, payload: update });
          
          // Update processing step - simplified approach
          if (message && status !== 'success') {
            dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: message });
            
            // Handle current processing and tool completion
            if (step === 'current_processing') {
              // Tool is currently being processed - show with processing status
              dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: message });
            } else if (step === 'tool_completed') {
              // Tool completed - add to completed steps list
              const stepUpdate = {
                id: update.id,
                step: message, // This is the tool name
                message: message,
                timestamp: update.timestamp,
                status: 'completed'
              };
              dispatch({ type: ACTIONS.ADD_STEP, payload: stepUpdate });
            }
          }

        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      dispatch({ type: ACTIONS.SET_SOCKET, payload: socket });
      return socket;
    };

        // Start initial connection
        connectWebSocket();

        return () => {
          isIntentionallyClosed = true;
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
          }
          // Close current socket if exists
          if (state.socket) {
            state.socket.close();
          }
        };
      } catch (error) {
        console.error('Failed to initialize connection:', error);
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to initialize connection' });
      }
    };

    initializeConnection();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages]);

  // Actions
  const actions = {
    sendMessage: async (message) => {
      if (!message.trim() || state.isProcessing) return;

      // T0: User click timestamp (start of end-to-end timing)
      const clickTimestamp = performance.now();
      const clickIsoTime = new Date().toISOString();

      console.log('🔍 sendMessage called with:', message);

      // Clear previous streaming data
      dispatch({ type: ACTIONS.CLEAR_STREAM_UPDATES });
      dispatch({ type: ACTIONS.CLEAR_STEPS });

      // Ensure we have a session for chat history persistence
      let sessionId = currentSessionId || chatHistoryRef.current.activeSessionId;
      if (!sessionId) {
        try {
          sessionId = await getOrCreateSession(state.dbId);
          console.log('📂 Created/got session:', sessionId);
          // IMPORTANT: Store session ID immediately in ref so WebSocket handler can use it
          // The hook state update might not propagate in time for the WebSocket response
          chatHistoryRef.current.activeSessionId = sessionId;
        } catch (err) {
          console.warn('Failed to create session, continuing without persistence:', err);
        }
      } else {
        // Ensure ref has the current session ID
        chatHistoryRef.current.activeSessionId = sessionId;
      }

      // Add user message
      const userMessage = createMessage(message, true);
      dispatch({ type: ACTIONS.ADD_MESSAGE, payload: userMessage });
      dispatch({ type: ACTIONS.SET_CURRENT_INPUT, payload: '' });

      // Save user message to database for chat history persistence
      // Pass sessionId explicitly since hook state may not have updated yet
      if (sessionId && saveMessageToDb) {
        saveMessageToDb({
          role: 'user',
          content: message
        }, sessionId).catch(err => console.warn('Failed to save user message to history:', err));
      }

      // Send to server via WebSocket
      const currentSocket = state.socket;
      if (currentSocket && state.connected && currentSocket.readyState === WebSocket.OPEN) {
        // T1: Before WebSocket send - capture timing delta
        const sendTimestamp = performance.now();
        const sendIsoTime = new Date().toISOString();
        const uiPrepDuration = Math.round(sendTimestamp - clickTimestamp);

        const requestData = {
          prompt: message,
          db_id: state.dbId,
          language: 'en',
          // Client timing data for end-to-end tracing
          client_timing: {
            user_click_at: clickIsoTime,
            ws_send_at: sendIsoTime,
            ui_prep_duration_ms: uiPrepDuration
          }
        };

        // NEW: Attach context if this is a follow-up query
        if (state.currentContext) {
          console.log('📤 Sending context with query:', state.currentContext);
          requestData.context = state.currentContext;
        }

        // NEW: Build and attach conversation history for multi-turn reformulation
        const conversationHistory = buildConversationHistory(state.messages);
        if (conversationHistory.length > 0) {
          console.log('📜 Sending conversation history:', conversationHistory.length, 'turns');
          requestData.conversation_history = conversationHistory;
        }

        try {
          currentSocket.send(JSON.stringify(requestData));
          dispatch({ type: ACTIONS.SET_PROCESSING, payload: true });
          console.log('✅ Message sent successfully');
        } catch (error) {
          console.error('❌ Error sending message:', error);
          dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to send message' });
          dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
        }
      } else {
        console.error('❌ Cannot send - socket missing or not connected');
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Connection not established' });
        dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
      }
    },

    adjustChart: async (originalMessage, adjustmentOptions) => {
      if (state.isProcessing) return;

      console.log('Chart adjustment requested:', adjustmentOptions);
      
      // Clear previous steps
      dispatch({ type: ACTIONS.CLEAR_STREAM_UPDATES });
      dispatch({ type: ACTIONS.CLEAR_STEPS });
      
      dispatch({ type: ACTIONS.SET_PROCESSING, payload: true });
      dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: 'جاري تعديل الرسم البياني...' });

      // Add step for StreamingIndicator
      const stepUpdate = {
        id: Date.now() + Math.random(),
        step: 'جاري تعديل الرسم البياني...',
        message: 'جاري تعديل الرسم البياني...',
        timestamp: new Date().toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        }),
        status: 'processing'
      };
      dispatch({ type: ACTIONS.ADD_STEP, payload: stepUpdate });

      try {
        const mainApiUrl = appConfig.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/adjust-chart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chart_type: adjustmentOptions.chart_type,
            x_axis: adjustmentOptions.x_axis,
            y_axis: adjustmentOptions.y_axis,
            color: adjustmentOptions.color,
            theta: adjustmentOptions.theta,
            sql_query: originalMessage.sql_query,
            column_names: originalMessage.column_names,
            results: originalMessage.results,
            question: 'Chart adjustment',
            current_chart_schema: originalMessage.visualization?.vega_lite_spec || {},
            language: 'en'
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          // Copy data from original message and merge with adjusted chart
          const adjustedData = {
            ...result.data,
            // Preserve original data for table display
            results: originalMessage.results,
            column_names: originalMessage.column_names,
            sql_query: originalMessage.sql_query,
            // Preserve suggestions from original
            suggestions: originalMessage.suggestions || [],
            explanation: originalMessage.explanation || result.data.explanation
          };

          const adjustedMessage = createMessage(
            result.data.final_answer || 'تم تعديل الرسم البياني بنجاح',
            false,
            adjustedData
          );
          dispatch({ type: ACTIONS.ADD_MESSAGE, payload: adjustedMessage });

          // Save adjusted chart message to database for persistence
          const { saveMessageToDb, activeSessionId } = chatHistoryRef.current;
          if (activeSessionId && saveMessageToDb) {
            saveMessageToDb({
              role: 'assistant',
              content: result.data.final_answer || 'تم تعديل الرسم البياني بنجاح',
              sql_query: originalMessage.sql_query || null,
              sql_execution_ms: null,
              sql_row_count: originalMessage.results?.length != null ? parseInt(originalMessage.results.length, 10) : null,
              chart_spec: result.data.visualization || null,
              metadata: {
                results: originalMessage.results || null,
                column_names: originalMessage.column_names || null,
                explanation: originalMessage.explanation || result.data.explanation || null,
                suggestions: originalMessage.suggestions || [],
                is_chart_adjustment: true,
                adjustment_options: adjustmentOptions || null
              }
            }, activeSessionId).catch(err => console.warn('Failed to save adjusted chart to history:', err));
          }
        } else {
          throw new Error('فشل تعديل الرسم البياني');
        }

      } catch (error) {
        console.error('Chart adjustment error:', error);
        const errorMessage = createMessage(
          `فشل تعديل الرسم البياني: ${error.message}`,
          false
        );
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: errorMessage });
      } finally {
        dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
        dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: '' });
        dispatch({ type: ACTIONS.CLEAR_STEPS });
      }
    },

    stopExecution: () => {
      console.log('🛑 Stop execution requested');
      dispatch({ type: ACTIONS.SET_PROCESSING, payload: false });
      dispatch({ type: ACTIONS.SET_PROCESSING_STEP, payload: '' });
      
      // Send interrupt message to server instead of closing connection
      const currentSocket = state.socket;
      if (currentSocket && state.connected && currentSocket.readyState === WebSocket.OPEN) {
        try {
          currentSocket.send(JSON.stringify({ action: 'interrupt' }));
          console.log('Interrupt signal sent to server');
        } catch (error) {
          console.error('Failed to send interrupt signal:', error);
        }
      }
    },

    clearMessages: () => {
      dispatch({ type: ACTIONS.CLEAR_MESSAGES });
      dispatch({ type: ACTIONS.CLEAR_STREAM_UPDATES });
      dispatch({ type: ACTIONS.CLEAR_STEPS });
    },

    setMessages: (messages) => {
      dispatch({ type: ACTIONS.SET_MESSAGES, payload: messages });
    },

    setCurrentInput: (input) => {
      dispatch({ type: ACTIONS.SET_CURRENT_INPUT, payload: input });
    },

    setDbId: (dbId) => {
      dispatch({ type: ACTIONS.SET_DB_ID, payload: dbId });
    },

    // Goal Mode actions
    setChatMode: (mode) => {
      dispatch({ type: ACTIONS.SET_CHAT_MODE, payload: mode });
      if (mode === 'normal') {
        dispatch({ type: ACTIONS.CLEAR_GOAL_STATE });
      }
    },

    setGoalText: (text) => {
      dispatch({ type: ACTIONS.SET_GOAL_TEXT, payload: text });
    },

    fetchGoalSuggestions: async (goalText) => {
      if (!goalText.trim()) return;

      dispatch({ type: ACTIONS.SET_LOADING_GOAL_SUGGESTIONS, payload: true });

      try {
        const mainApiUrl = appConfig.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/suggest-by-goal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            goal: goalText,
            db_id: state.dbId
          })
        });

        if (!response.ok) {
          throw new Error('Failed to get suggestions');
        }

        const data = await response.json();
        dispatch({
          type: ACTIONS.SET_GOAL_SUGGESTIONS,
          payload: {
            suggestions: data.suggestions,
            explanation: data.explanation
          }
        });
      } catch (error) {
        console.error('Goal suggestion error:', error);
        dispatch({ type: ACTIONS.SET_LOADING_GOAL_SUGGESTIONS, payload: false });
        dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      }
    },

    selectGoalSuggestion: (suggestion) => {
      // Send the query from the selected suggestion
      actions.sendMessage(suggestion.query);
    },

    clearGoalState: () => {
      dispatch({ type: ACTIONS.CLEAR_GOAL_STATE });
    }
  };

  return (
    <ChatContext.Provider value={{
      ...state,
      actions,
      messagesEndRef
    }}>
      {children}
    </ChatContext.Provider>
  );
}

// Custom hook to use the chat context
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}